import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { isMobileDevice } from "./eie/hooks/cameraConstraints";

const DURATION_MS = 2000;
const PARTICLE_COUNT = isMobileDevice() ? 150 : 520;

// Wave travels from top-left to bottom-right as a diagonal band
// WAVE_SPEED: fraction of the diagonal crossed per second (1.0 = full diagonal in 1s)
const WAVE_SPEED = 0.55;   // crosses in ~1.8s of the 2s animation
const WAVE_WIDTH = 0.18;   // band half-thickness as fraction of diagonal
const WAVE_PEAK_PEX = 0.45; // progress at which onPeak fires (just as wave centre crosses mid-screen)

function hexToRgb(hex) {
  if (!hex) return { r: 139, g: 92, b: 246 };
  const clean = hex.replace("#", "");
  const full = clean.length === 3
    ? `${clean[0]}${clean[0]}${clean[1]}${clean[1]}${clean[2]}${clean[2]}`
    : clean;

  const value = Number.parseInt(full, 16);
  if (Number.isNaN(value)) return { r: 139, g: 92, b: 246 };

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

// 4-point star sparkle
function drawSparkle(ctx, cx, cy, r) {
  const inner = r * 0.32;
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const angle = (i * Math.PI) / 4 - Math.PI / 4;
    const len = i % 2 === 0 ? r : inner;
    const x = cx + Math.cos(angle) * len;
    const y = cy + Math.sin(angle) * len;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

// ── Wave rendering ────────────────────────────────────────────────────────────
// The wave is a diagonal band moving from (0,0) toward (width,height).
// We project each pixel onto the diagonal axis using the dot product
// with the normalised direction vector (cos45°, sin45°) = (√2/2, √2/2).
// The diagonal length = √(w²+h²). The wavefront centre at time t is at
//   center = diagonal * progress * WAVE_SPEED
// We draw a linear gradient rect that fades in/out around that centre.
function drawWave(ctx, width, height, progress, r, g, b) {
  const diagonal = Math.sqrt(width * width + height * height);
  // Centre of the wavefront along the diagonal
  const center = diagonal * (progress / WAVE_SPEED);
  if (center < 0) return;

  const bandPx = WAVE_WIDTH * diagonal;
  const leadingEdge = center + bandPx;
  const trailingEdge = center - bandPx;

  // Peak opacity of the wave band — fades in during first 20%, out after 80%
  const waveEnvelope = progress < 0.12
    ? progress / 0.12
    : progress > 0.80
    ? (1 - progress) / 0.20
    : 1.0;
  const waveAlpha = 0.13 * waveEnvelope;   // subtle, not overwhelming

  // We can't clip a diagonal band cheaply with fillRect, so we use a
  // linear gradient that goes transparent → colour → transparent along
  // the diagonal direction. We extend the rect beyond screen edges so
  // the gradient fills the whole canvas at the correct angle.
  const extend = diagonal;
  const gx1 = (trailingEdge / diagonal) * width - extend;
  const gy1 = (trailingEdge / diagonal) * height - extend;
  const gx2 = (leadingEdge  / diagonal) * width  + extend;
  const gy2 = (leadingEdge  / diagonal) * height + extend;

  const grad = ctx.createLinearGradient(gx1, gy1, gx2, gy2);
  grad.addColorStop(0,    `rgba(${r},${g},${b},0)`);
  grad.addColorStop(0.25, `rgba(${r},${g},${b},${waveAlpha})`);
  grad.addColorStop(0.50, `rgba(${r},${g},${b},${waveAlpha * 1.6})`);
  grad.addColorStop(0.75, `rgba(${r},${g},${b},${waveAlpha})`);
  grad.addColorStop(1,    `rgba(${r},${g},${b},0)`);

  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);
}

function toDisplayEmotionLabel(value) {
  if (!value || typeof value !== "string") return "";
  const raw = value.replace(/[_-]+/g, " ").trim();
  if (!raw) return "";
  return raw
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function drawEmotionCaption(ctx, width, height, progress, r, g, b, label) {
  if (!label) return;

  const p = clamp((progress - 0.10) / 0.80, 0, 1);
  if (p <= 0) return;

  // Fade 0 -> 1 in first third, then 1 -> 0 by the end.
  const fadeEnvelope = p < 0.32
    ? p / 0.32
    : p > 0.70
    ? (1 - p) / 0.30
    : 1;
  const alpha = 0.82 * fadeEnvelope;
  if (alpha <= 0) return;

  const caption = label;
  const fontSize = Math.round(Math.max(20, Math.min(38, width * 0.03)));

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `600 ${fontSize}px "Montserrat", "Segoe UI", sans-serif`;

  // Move upward from ~2/3 screen height to ~1/3 during the caption lifetime.
  const startY = height * 0.66;
  const endY = height * 0.34;
  const y = startY + (endY - startY) * p;
  ctx.font = `600 ${fontSize}px "Cinzel", "Cormorant Garamond", "Montserrat", "Segoe UI", sans-serif`;
  ctx.letterSpacing = "1.2px";

  const textWidth = ctx.measureText(caption).width;
  const hazeRadius = Math.max(56, textWidth * 0.42);

  // Atmospheric haze behind text, no badge plate.
  const haze = ctx.createRadialGradient(width / 2, y, 0, width / 2, y, hazeRadius);
  haze.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha * 0.22})`);
  haze.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
  ctx.fillStyle = haze;
  ctx.beginPath();
  ctx.arc(width / 2, y, hazeRadius, 0, Math.PI * 2);
  ctx.fill();

  const shimmer = 0.5 + 0.5 * Math.sin(p * Math.PI * 2.4);
  const shineAlpha = Math.min(0.95, alpha * (0.7 + shimmer * 0.45));
  const textGrad = ctx.createLinearGradient(
    width / 2 - textWidth / 2,
    y - fontSize,
    width / 2 + textWidth / 2,
    y + fontSize,
  );
  textGrad.addColorStop(0, `rgba(255,255,255,${Math.min(1, shineAlpha + 0.18)})`);
  textGrad.addColorStop(0.42, `rgba(${Math.min(255, r + 90)}, ${Math.min(255, g + 90)}, ${Math.min(255, b + 90)}, ${shineAlpha})`);
  textGrad.addColorStop(1, `rgba(214,236,255,${Math.max(0.45, shineAlpha - 0.14)})`);

  ctx.shadowColor = `rgba(${r},${g},${b},${Math.min(0.9, alpha * 1.2)})`;
  ctx.shadowBlur = 30;
  ctx.fillStyle = textGrad;
  ctx.fillText(caption, width / 2, y);

  // Fine luxury underline with soft glow.
  const underlineW = Math.max(58, Math.min(240, textWidth * 0.52));
  const underlineY = y + fontSize * 0.58;
  const lineGrad = ctx.createLinearGradient(
    width / 2 - underlineW / 2,
    underlineY,
    width / 2 + underlineW / 2,
    underlineY,
  );
  lineGrad.addColorStop(0, `rgba(${r},${g},${b},0)`);
  lineGrad.addColorStop(0.5, `rgba(${r},${g},${b},${Math.min(0.85, alpha * 0.95)})`);
  lineGrad.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.strokeStyle = lineGrad;
  ctx.lineWidth = 1.35;
  ctx.beginPath();
  ctx.moveTo(width / 2 - underlineW / 2, underlineY);
  ctx.lineTo(width / 2 + underlineW / 2, underlineY);
  ctx.stroke();

  ctx.restore();
}

const EmotionSmokeOverlay = forwardRef(function EmotionSmokeOverlay(_props, ref) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const animationRef = useRef(null);

  const stopAnimation = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    animationRef.current = null;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  useImperativeHandle(ref, () => ({
    triggerSmoke(accentColor, _themeName, onPeak, emotionLabel) {
      if (typeof window === "undefined") return;
      stopAnimation();
      resizeCanvas();

      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const { r, g, b } = hexToRgb(accentColor);
      // Lighter variant for sparkle highlights
      const sr = Math.round(r + (255 - r) * 0.60);
      const sg = Math.round(g + (255 - g) * 0.60);
      const sb = Math.round(b + (255 - b) * 0.60);

      const width = window.innerWidth;
      const height = window.innerHeight;

      const particles = Array.from({ length: PARTICLE_COUNT }, () => {
        const isSparkle = Math.random() < 0.35;
        const startPct = Math.random() * 0.35;
        const lifespan = 0.32 + Math.random() * 0.52;
        const endPct = Math.min(startPct + lifespan, 1);

        // Heavily upward-biased drift; narrower spread = more coherent dust cloud
        const upwardBias = -Math.PI / 2;
        const spread = Math.PI * 0.70;
        const driftAngle = upwardBias + (Math.random() - 0.5) * spread * 2;

        return {
          spawnX: Math.random() * width,
          spawnY: height * (0.15 + Math.random() * 0.85),
          driftAngle,
          // Slower, more floaty than before
          speed: isSparkle
            ? 0.022 + Math.random() * 0.040
            : 0.010 + Math.random() * 0.025,
          // Dust stays subtle; sparkles are larger so they remain visible over hero imagery.
          size: isSparkle
            ? 2.2 + Math.random() * 3.2
            : 0.5 + Math.random() * 1.3,
          startPct,
          endPct,
          twinkleFreq: isSparkle ? 4 + Math.random() * 6 : 1 + Math.random() * 2.5,
          twinklePhase: Math.random() * Math.PI * 2,
          wobble: (Math.random() - 0.5) * 0.0014,
          isSparkle,
        };
      });

      animationRef.current = {
        startedAt: performance.now(),
        peakTriggered: false,
        particles,
        r, g, b,
        sr, sg, sb,
        emotionLabel: toDisplayEmotionLabel(emotionLabel),
        onPeak,
      };

      const tick = (timestamp) => {
        const current = animationRef.current;
        if (!current) return;

        const elapsed = timestamp - current.startedAt;
        const progress = clamp(elapsed / DURATION_MS, 0, 1);

        ctx.clearRect(0, 0, width, height);

        // ── Wave pass ──────────────────────────────────────────────────────
        drawWave(ctx, width, height, progress, current.r, current.g, current.b);
        // ── Dust particles ─────────────────────────────────────────────────
        for (const p of current.particles) {
          const localP = clamp(
            (progress - p.startPct) / (p.endPct - p.startPct),
            0, 1,
          );
          if (localP <= 0) continue;

          const envelope = localP < 0.30
            ? localP / 0.30
            : localP > 0.68
            ? (1 - localP) / 0.32
            : 1;

          const twinkle = 0.50 + 0.50 * Math.sin(p.twinkleFreq * localP * Math.PI * 2 + p.twinklePhase);
          const opacity = envelope * twinkle * (p.isSparkle ? 1.18 : 0.55);

          const driftAngle = p.driftAngle + p.wobble * elapsed;
          const d = p.speed * elapsed;
          const x = p.spawnX + Math.cos(driftAngle) * d;
          const y = p.spawnY + Math.sin(driftAngle) * d;

          if (p.isSparkle) {
            ctx.globalCompositeOperation = "lighter";
            ctx.shadowColor = `rgba(${current.sr},${current.sg},${current.sb},${Math.min(0.95, opacity)})`;
            ctx.shadowBlur = 16;
            ctx.fillStyle = `rgba(${current.sr},${current.sg},${current.sb},${opacity})`;
            drawSparkle(ctx, x, y, p.size);
            ctx.shadowBlur = 0;
            ctx.globalCompositeOperation = "source-over";
          } else {
            ctx.fillStyle = `rgba(${current.r},${current.g},${current.b},${opacity})`;
            ctx.beginPath();
            ctx.arc(x, y, p.size, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        // Draw caption above particles so it remains readable during dense bursts.
        drawEmotionCaption(
          ctx,
          width,
          height,
          progress,
          current.r,
          current.g,
          current.b,
          current.emotionLabel,
        );

        if (!current.peakTriggered && progress >= WAVE_PEAK_PEX) {
          current.peakTriggered = true;
          current.onPeak?.();
        }

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }

        stopAnimation();
      };

      rafRef.current = requestAnimationFrame(tick);
    },
  }), []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    resizeCanvas();
    const onResize = () => { resizeCanvas(); };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      stopAnimation();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 9998,
      }}
    />
  );
});

export default EmotionSmokeOverlay;
